import { useEffect, useState, type ReactNode } from 'react'
import { Link, useParams } from 'react-router-dom'
import { ChevronLeft, SlidersHorizontal } from 'lucide-react'
import { useT } from '@/i18n'
import { PageHeader } from '@/shell/AppShell'
import { fetchGroup, type AdminGroupDetail } from '@/lib/adapters'
import { PERMISSIONS } from '@/lib/permissions'
import { InfoRow } from '@/components/DataDisplay'
import { StatusBadge } from '@/components/StatusBadge'
import { ErrorState, PermissionDenied, Skeleton } from '@/components/States'
import { useAuth } from '@/providers/AuthProvider'

/**
 * A4.07 — Sozlamalar snapshot'i. GET /admin/groups/:id. Faqat-o'qish moderatsiya-
 * konfiguratsiyasi: imzolangan postlar / sekin-rejim / qo'shilish-so'rovi /
 * kontent-himoyasi / tarix-ko'rinishi / forum. Settings ustunlari admin `GetGroup`
 * SELECT'ga qo'shilgach (lane-A backend patch) to'ladi; shu paytgacha «—».
 */
export function GroupSettings() {
  const t = useT()
  const { can } = useAuth()
  const { id = '' } = useParams()
  const [group, setGroup] = useState<AdminGroupDetail | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<Error | null>(null)
  const [reload, setReload] = useState(0)

  useEffect(() => {
    const ac = new AbortController()
    let alive = true
    setLoading(true)
    setError(null)
    fetchGroup(Number(id), ac.signal)
      .then((g) => {
        if (alive) setGroup(g)
      })
      .catch((e: unknown) => {
        if (!alive || (e instanceof DOMException && e.name === 'AbortError')) return
        setError(e instanceof Error ? e : new Error(String(e)))
      })
      .finally(() => {
        if (alive) setLoading(false)
      })
    return () => {
      alive = false
      ac.abort()
    }
  }, [id, reload])

  if (!can(PERMISSIONS.USERS_VIEW)) return <PermissionDenied permission={PERMISSIONS.USERS_VIEW} />

  const slowMode =
    group?.slow_mode_seconds == null
      ? '—'
      : group.slow_mode_seconds === 0
        ? t.common.no
        : `${group.slow_mode_seconds}s`

  return (
    <>
      <PageHeader
        title={t.groups.settingsTitle}
        screenId="A4.07"
        actions={
          <Link
            to={`/groups/${id}`}
            className="inline-flex items-center gap-1 rounded-md border border-border-default bg-surface-2 px-2.5 py-1 text-sm text-secondary hover:text-primary"
          >
            <ChevronLeft size={13} />
            {t.groups.cardTitle}
          </Link>
        }
      />
      <div className="min-h-0 flex-1 overflow-auto p-3">
        {loading ? (
          <div className="max-w-2xl space-y-2 rounded-lg border border-border-default bg-surface-2 p-4">
            {Array.from({ length: 6 }, (_, i) => (
              <Skeleton key={i} className="h-4 w-2/3" />
            ))}
          </div>
        ) : error ? (
          <ErrorState code={error.message} onRetry={() => setReload((n) => n + 1)} />
        ) : !group ? (
          <ErrorState title={t.states.notFoundTitle} />
        ) : (
          <div className="max-w-2xl rounded-lg border border-border-default bg-surface-2 p-4">
            <div className="mb-3 flex items-center gap-2 text-secondary">
              <SlidersHorizontal size={16} className="text-accent" />
              <span className="text-sm">
                {group.type === 'channel' ? t.groups.channel : t.groups.group}
              </span>
            </div>
            <BoolRow label={t.groups.forumMode} on={group.is_forum} t={t} />
            <BoolRow label={t.groups.signMessages} on={group.sign_messages} t={t} />
            <InfoRow label={t.groups.slowMode}>{slowMode}</InfoRow>
            <BoolRow label={t.groups.joinByRequest} on={group.join_by_request} t={t} />
            <BoolRow label={t.groups.contentProtected} on={group.content_protected} t={t} />
            <BoolRow label={t.groups.historyVisible} on={group.history_visible} t={t} />
            <InfoRow label={t.groups.topicLayout}>{group.topic_layout || '—'}</InfoRow>
          </div>
        )}
      </div>
    </>
  )
}

function BoolRow({ label, on, t }: { label: string; on: boolean | undefined; t: ReturnType<typeof useT> }): ReactNode {
  return (
    <InfoRow label={label}>
      {on === undefined ? (
        <span className="text-tertiary">—</span>
      ) : (
        <StatusBadge status={on ? 'active' : 'deleted'} label={on ? t.common.yes : t.common.no} />
      )}
    </InfoRow>
  )
}
